import React, { useState } from 'react';

import { QuestionCard } from './card';
import { QuestionSummary } from './summary';
import { useConversationContext } from '../../../conversation_context';

import type { AskUserQuestionsToolCall } from './types';

// How the card was spent, in the shape the server will persist it in, so the branches below read it
// the same whether it happened just now or on a previous visit.
type Spent = Pick<AskUserQuestionsToolCall['metadata'], 'answers' | 'dismissed'>;

interface Props {
  superseded: boolean;
  toolCall: AskUserQuestionsToolCall;
}

export function Question({ superseded, toolCall }: Props) {
  const { answerQuestions, busy, dismissQuestions } = useConversationContext();
  const { answerPath, questions } = toolCall.metadata;

  // Held here until the server's copy comes back with a later load, rather than patched into the
  // transcript: this is the only thing that renders it, and the metadata it falls back to is where
  // the same state lives on every visit after the first.
  const [spent, setSpent] = useState<Spent | null>(null);
  const { answers, dismissed } = spent ?? toolCall.metadata;

  async function resolve(next: Spent, send: () => Promise<boolean>) {
    setSpent(next);
    const resolved = await send();

    if (!resolved) {
      setSpent(null);
    }
  }

  // Closing the card is its own answer - the questions were the wrong ones - so it reads differently
  // from a card the user simply talked past. Checked first because spending a card always writes
  // both keys, which leaves a dismissal carrying a row of empty answers.
  if (dismissed) {
    return <QuestionSummary answers={null} note="Closed without answering" questions={questions} />;
  }

  if (answers) {
    return <QuestionSummary answers={answers} note={null} questions={questions} />;
  }

  // Only a message the user typed past the card retires it. An attempt that failed leaves it live,
  // so they can answer again from the same card rather than starting over.
  if (superseded) {
    return <QuestionSummary answers={null} note="Not answered" questions={questions} />;
  }

  return (
    <QuestionCard
      busy={busy}
      questions={questions}
      onDismiss={() => void resolve({ dismissed: true }, () => dismissQuestions(answerPath))}
      onSubmit={submitted => void resolve({ answers: submitted }, () => answerQuestions(answerPath, submitted))}
    />
  );
}
